import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from "@/lib/api";

export default function MapView({ stops = [], routeGeoJson, height = "420px" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [token, setToken] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mapbox/config");
        if (r.data?.public_token) setToken(r.data.public_token);
        else setMissing(true);
      } catch {
        setMissing(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!token || mapRef.current || !containerRef.current) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [10.4515, 51.1657],
      zoom: 5.2,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.on("load", () => {
      mapRef.current = map;
      updateMarkers(map, stops);
      if (routeGeoJson) addRoute(map, routeGeoJson);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapRef.current) return;
    updateMarkers(mapRef.current, stops);
  }, [stops]);

  useEffect(() => {
    if (!mapRef.current || !routeGeoJson) return;
    const m = mapRef.current;
    if (m.getSource("route")) m.getSource("route").setData(routeGeoJson);
    else addRoute(m, routeGeoJson);
  }, [routeGeoJson]);

  const updateMarkers = (map, list) => {
    markersRef.current.forEach((mk) => mk.remove());
    markersRef.current = [];
    list.forEach((s, idx) => {
      const el = document.createElement("div");
      el.className = "animate-pop-in";
      el.style.cssText = `width:30px;height:30px;border-radius:50%;background:var(--tonneva-primary);border:2px solid white;box-shadow:0 4px 12px rgba(0,71,255,0.4);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:13px;font-family:'IBM Plex Mono',monospace;`;
      el.textContent = String(idx + 1);
      const marker = new mapboxgl.Marker(el).setLngLat([s.lng, s.lat]).addTo(map);
      if (s.label) marker.setPopup(new mapboxgl.Popup({ offset: 24 }).setText(s.label));
      markersRef.current.push(marker);
    });
    if (list.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      list.forEach((s) => bounds.extend([s.lng, s.lat]));
      map.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 600 });
    }
  };

  const addRoute = (map, geo) => {
    map.addSource("route", { type: "geojson", data: geo });
    map.addLayer({
      id: "route-line",
      type: "line",
      source: "route",
      paint: { "line-color": "#FF4500", "line-width": 4 },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  };

  if (missing) {
    return (
      <div className="border border-dashed border-black/20 bg-zinc-50 flex items-center justify-center text-center p-8" style={{ height }} data-testid="map-fallback">
        <div>
          <div className="font-heading text-lg font-bold tracking-tight">Karte deaktiviert</div>
          <p className="text-xs text-[var(--tonneva-muted)] mt-2 max-w-sm">
            Mapbox-Token nicht konfiguriert. Setzen Sie <code className="font-mono text-[10px]">MAPBOX_PUBLIC_TOKEN</code> in <code className="font-mono text-[10px]">/app/backend/.env</code>, um die interaktive Karte zu aktivieren.
          </p>
          {stops.length > 0 && (
            <div className="mt-4 text-left text-xs space-y-1 max-w-sm mx-auto">
              {stops.map((s, i) => (
                <div key={s.id || s.customer_id || `${s.lng},${s.lat}`} className="flex items-center gap-2 border border-black/10 bg-white px-3 py-1.5">
                  <span className="font-mono font-bold text-[var(--tonneva-primary)]">{i + 1}.</span>
                  <span>{s.label || `${s.lng}, ${s.lat}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return <div ref={containerRef} style={{ height, width: "100%", borderRadius: 0 }} className="border border-black/10" data-testid="map-container" />;
}
